import { Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { store } from 'redux/store';
import {
    dateFormat,
    debounce,
    formatAmount,
    formatDateAndTime,
    getDefaultCompanyId,
    getTodayFilter,
    parseCSV,
    timeFormat,
} from 'utils/helpers';
import TextInput from 'components/Common/TextInput';
import CustomButton from 'components/Common/CustomButton';
import MenuList from 'components/Common/MenuList';
import Table from 'components/Common/StyledTable';
import { DateFilter } from 'components/Common/DateFilter';
import DateAndTimePicker from 'components/Common/DateAndTimePicker';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import DownloadCSV from './DownloadCSV';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import client from 'api/apiClient';
import { oldGloryBaseUrl } from 'config/apiConfig';

const rendererMap:any = {
    Date: ({ value }: StringRecord) => dateFormat(value),
    Time: ({ value }: StringRecord) => timeFormat(value, 'hh:mm A'),
    Total: ({ value, data }: StringRecord) => formatAmount(value, 'dollar', data?.type),
    Note: ({ value }: StringRecord) => {
        return (
            <Tooltip title={value}>
                <Typography fontSize={'16px'} textOverflow={'ellipsis'} maxWidth={'20ch'} overflow={'hidden'} whiteSpace={'nowrap'}>
                    {value}
                </Typography>
            </Tooltip>
        );
    },
};

type StringRecord = Record<string, any>;
export interface ParseCSVResponse {
    data: StringRecord[];
    meta: {
        fields: string[];
    };
}
interface FormData {
    sortOrder?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
}
const ExportCSVTable = () => {
    const navigate = useNavigate();
    const companyId = useSelector(getDefaultCompanyId);
    const dispatch = useDispatch<typeof store.dispatch>();
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState<StringRecord[] | []>([]);
    const [fields, setFields] = useState<string[] | []>([]);
    const [filter, setFilter] = useState<any>({ ...getTodayFilter() });
    const [searchField, setSearchField] = useState<string>('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const isDisabled = !records?.length;
    const {
        control,
        watch,
        formState: { errors },
        getValues,
        trigger,
        setValue,
        clearErrors,
    } = useForm({
        defaultValues: {
            sortOrder: 'ASC',
            customDateFrom: '',
            customDateTo: '',
        },
    });
    const hasError = !!Object?.keys(errors)?.length;
    const formValues = getValues();
    const { sortOrder, customDateFrom, customDateTo } = formValues;
    const { dayFrom, dayTo, value } = filter;
    const isDateValid = !(dayFrom && dayTo) || hasError;

    const csvHeaders = useMemo(() => {
        return fields.map((f) => ({
            label: f,
            key: f,
        }));
    }, [fields]);

    const formData: FormData = {
        sortOrder,
    };

    const buildColumns = useMemo(() => {
        return fields.map((f) => {
            let render;
            if (rendererMap?.[f]) {
                render = rendererMap[f];
            }
            return { id: f, label: f, render };
        });
    }, [fields, records]);

    const fetchTransactionRecords = async () => {
        setLoading(true);
        try {
            const response = await client.post(`${oldGloryBaseUrl}/report/v2/getAccountTransactionReport`, { ...formData, companyId });
            const parsedData: ParseCSVResponse = parseCSV(response as unknown as string);
            if (response) {
                const records = parsedData?.data || [];
                const fields = parsedData?.meta?.fields || [];
                setRecords(records);
                setFields(fields);
            }
        } catch (error) {
            console.error('Error fetching records:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTransactionFilter = async () => {
        if (value === 'custom' && (!dayFrom || !dayTo)) return;

        if (value !== 'all') {
            formData.dateFrom = formatDateAndTime(dayFrom);
            formData.dateTo = formatDateAndTime(dayTo);
        }

        if (debouncedSearchText) {
            formData.search = debouncedSearchText;
        }

        fetchTransactionRecords();
    };
    const debouncedSetSearchText = useCallback(debounce(setDebouncedSearchText, 700), []);

    useEffect(() => {
        debouncedSetSearchText(searchField);
    }, [searchField, debouncedSetSearchText]);

    useEffect(() => {
        if (value === 'custom') {
            setFilter({
                dayFrom: customDateFrom,
                dayTo: customDateTo,
                value: 'custom',
            });
        }
    }, [customDateFrom, customDateTo]);

    useEffect(() => {
        if (value !== 'custom') {
            clearErrors('customDateFrom');
            clearErrors('customDateTo');
            setValue('customDateFrom', '');
            setValue('customDateTo', '');
        }
        if (Object?.keys(errors)?.length) return;
        handleTransactionFilter();
    }, [dispatch, filter, sortOrder, debouncedSearchText]);

    return (
        <Stack minHeight="700px" direction={{ xs: 'column', sm: 'row' }} sx={{ backgroundColor: 'white', p: 2, mx: 2 }}>
            <Stack width="100%">
                <Stack alignItems={'center'} gap={2} direction={'row'}>
                    <IconButton onClick={() => navigate(-1)}>
                        <KeyboardBackspaceIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: 26 }}>Export</Typography>
                </Stack>
                <Divider />

                <Stack my={3} alignItems="center" justifyContent="space-between" direction="row">
                    <Stack width={'50%'} alignItems="center" direction="row">
                        <DateFilter
                            showCustomOpts={true}
                            filter={filter}
                            setFilter={(prop: any) => {
                                if (prop.value === 'custom') {
                                    setRecords([]);
                                }
                                setFilter(prop);
                            }}
                        />
                        <TextInput
                            placeholder="Search"
                            disabled={isDateValid}
                            icon={
                                debouncedSearchText && (
                                    <IconButton size="small" sx={{ width: 10, height: 10 }} onClick={() => setSearchField('')}>
                                        {<CloseOutlinedIcon />}
                                    </IconButton>
                                )
                            }
                            value={searchField}
                            iconPosition="end"
                            width={'100%'}
                            onChange={(e: any) => {
                                setSearchField(e.target.value);
                            }}
                        />
                    </Stack>

                    <Stack width={'25%'} alignItems="center" justifyContent="end" direction="row">
                        <Stack width={'fit'} gap={2}>
                            <DownloadCSV disabled={isDisabled} data={records} headers={csvHeaders} fileName="Account-activity">
                                <CustomButton disabled={isDisabled} sx={{ width: '200px' }}>
                                    Download
                                </CustomButton>
                            </DownloadCSV>
                        </Stack>
                        <Controller
                            name="sortOrder"
                            control={control}
                            render={({ field }) => (
                                <MenuList
                                    menuItems={[
                                        {
                                            item: 'ASC',
                                            onClick: () => field.onChange('ASC'),
                                            disabled: watch('sortOrder') === 'ASC' || isDateValid,
                                        },
                                        {
                                            item: 'DSC',
                                            onClick: () => field.onChange('DSC'),
                                            disabled: field.value === 'DSC' || isDateValid,
                                        },
                                    ]}
                                />
                            )}
                        />
                    </Stack>
                </Stack>

                {filter?.value === 'custom' && (
                    <form>
                        <Stack mx={2} mb={2} alignItems={'center'} justifyContent="space-between" direction="row">
                            <Stack minHeight={140}>
                                <Typography fontWeight={600}>From</Typography>
                                <Controller
                                    name="customDateFrom"
                                    control={control}
                                    rules={{
                                        required: 'Date from is required',
                                        validate: {
                                            isValidDate: (value) => dayjs(value).isValid() || 'Invalid date format',
                                            isBeforeDateFrom: (value) =>
                                                dayjs(value).isBefore(dayjs(watch('customDateTo'))) ||
                                                'Date to cannot be later than Date To',
                                        },
                                    }}
                                    render={({ field }) => {
                                        return (
                                            <DateAndTimePicker
                                                helperText={(errors?.customDateFrom as any)?.message}
                                                error={!!errors?.customDateFrom}
                                                date={watch('customDateFrom')}
                                                setDateAndTime={(props) => {
                                                    field.onChange(props);
                                                    !!watch('customDateTo') && trigger();
                                                }}
                                                defaultNull={!watch('customDateFrom')}
                                            />
                                        );
                                    }}
                                />
                            </Stack>
                            <Stack minHeight={140}>
                                <Typography fontWeight={600}>To</Typography>
                                <Controller
                                    name="customDateTo"
                                    control={control}
                                    rules={{
                                        required: 'Date to is required',
                                        validate: {
                                            isValidDate: (value) => dayjs(value).isValid() || 'Invalid date format',
                                            isPastDate: (value) => dayjs(value).isBefore(dayjs()) || 'Date to cannot be a future date',
                                            isAfterDateFrom: (value) =>
                                                dayjs(value).isAfter(dayjs(watch('customDateFrom'))) ||
                                                'Date to cannot be earlier than Date from',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <DateAndTimePicker
                                            helperText={(errors?.customDateTo as any)?.message}
                                            error={!!errors?.customDateTo}
                                            date={watch('customDateTo')}
                                            defaultNull={!watch('customDateTo')}
                                            setDateAndTime={(props) => {
                                                field.onChange(props);
                                                !!watch('customDateFrom') && trigger();
                                            }}
                                        />
                                    )}
                                />
                            </Stack>
                        </Stack>
                    </form>
                )}

                <Table
                    emptyRecordsMessage={isDateValid ? 'Please select a date range to view the records.' : ''}
                    loading={loading}
                    columns={buildColumns}
                    data={records}
                    totalCount={records?.length}
                    showArrowCell={false}
                />
            </Stack>
        </Stack>
    );
};

export default ExportCSVTable;
